"use client";

import {
  BadgePercent,
  CheckCircle2,
  ChevronDown,
  Copy,
  LoaderCircle,
  Minus,
  Package,
  Gift,
  Plus,
  ShoppingCart,
  Trash2,
  X,
} from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";

import { publicOnlineSettings, type OnlineSettings } from "@/lib/food-online";
import { useDeliveryQuote } from "./useDeliveryQuote";
import { ApiError } from "@/lib/api";
import {
  type PublicCouponValidationResponse,
  validatePublicCoupon,
} from "@/lib/coupons";
import { formatCurrency } from "@/lib/order";
import { buildPixPayload } from "@/lib/pix";
import { cartItemPayload, compositionKey, fractionLabel } from "@/lib/food-configuration";
import {
  createPublicOrder,
  previewPublicOrderPix,
  type GiftPromotionQuote,
} from "@/lib/store";
import { lookupPostalCode } from "@/lib/postal-code";
import { quoteShipping } from "@/lib/shipping";
import type { ShippingQuoteOption } from "@/types/shipping";
import type { CartItem } from "@/types/cart";
import type { PaymentMethod, PublicOrderPayload } from "@/types/order";
import type { CompanyBusinessType } from "@/types/company";
import type { PublicCompany } from "@/types/store";

const CUSTOMER_STORAGE_KEY = "fornecedor_customer_data";

type SavedCustomer = {
  customer_name: string;
  customer_phone: string;
};

type PixCheckoutState = {
  payload: PublicOrderPayload;
  total: string;
  pixPayload: string | null;
  qrDataUrl: string | null;
  pixKey: string;
};

type FoodDeliveryType = "local_delivery" | "pickup";

function formatCep(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 8);

  if (digits.length <= 5) {
    return digits;
  }

  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

function normalizeDeliveryRegionName(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLocaleLowerCase("pt-BR");
}

function getCartItemKey(item: CartItem) {
  const optionKey = (item.selected_options ?? [])
    .map((option) => option.option_id)
    .sort((a, b) => a - b)
    .join(",");

  const observationKey = encodeURIComponent(
    (item.observation ?? "").trim().toLowerCase(),
  );

  return `${item.product.id}:${
    item.variation?.id ?? "simple"
  }:${optionKey}:${observationKey}:${compositionKey(item.composition_selections)}`;
}

function getCartItemUnitPrice(item: CartItem) {
  if (item.quoted_unit_price != null) return Number(item.quoted_unit_price);
  const basePrice = Number(
    item.variation?.current_price ?? item.product.current_price,
  );

  const optionsTotal = (item.selected_options ?? []).reduce(
    (sum, option) => sum + Number(option.price_delta),
    0,
  );

  return basePrice + optionsTotal;
}

function getApiErrorMessage(error: unknown) {
  if (!(error instanceof ApiError)) {
    return "Não foi possível registrar o pedido.";
  }

  if (error.data && typeof error.data === "object") {
    const storeAvailability = (
      error.data as {
        store_availability?: {
          message?: unknown;
        };
      }
    ).store_availability;

    if (storeAvailability && typeof storeAvailability.message === "string") {
      return storeAvailability.message;
    }

    for (const value of Object.values(error.data)) {
      if (Array.isArray(value) && typeof value[0] === "string") {
        return value[0];
      }

      if (typeof value === "string") {
        return value;
      }
    }
  }

  return error.message;
}

export default function CartDrawer({
  slug,
  companyName,
  companyCity,
  businessType,
  foodAvailability,
  localDeliveryEnabled,
  deliveryFeeMode,
  localDeliveryFee,
  pickupEnabled,
  acceptPix,
  pixKey,
  acceptCardOnDelivery,
  acceptCashOnDelivery,
  deliveryRegions,
  items,
  total,
  onClose,
  onUpdateQuantity,
  onRemove,
  onClear,
  giftPromotions = [],
  quotePending = false, quoteError, onRevalidate,
}: {
  slug: string;
  companyName: string;
  companyCity: string;
  businessType: CompanyBusinessType;
  foodAvailability: PublicCompany["food_availability"];
  localDeliveryEnabled: boolean;
  deliveryFeeMode: "fixed" | "region";
  localDeliveryFee: string;
  pickupEnabled: boolean;
  acceptPix: boolean;
  pixKey: string;
  acceptCardOnDelivery: boolean;
  acceptCashOnDelivery: boolean;
  deliveryRegions: Array<{
    id: number;
    name: string;
    fee: string;
  }>;
  items: CartItem[];
  total: number;
  giftPromotions?: GiftPromotionQuote[];
  quotePending?: boolean;
  quoteError?: string;
  onRevalidate?: () => Promise<boolean>;
  onClose: () => void;
  onUpdateQuantity: (itemKey: string, quantity: number) => void;
  onRemove: (itemKey: string) => void;
  onClear: () => void;
}) {
  const [online, setOnline] = useState<OnlineSettings | null>(null);
  const [giftChoices, setGiftChoices] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    const activeIds = new Set(
      giftPromotions.map((promotion) => promotion.public_id),
    );

    setGiftChoices((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([publicId]) =>
          activeIds.has(publicId),
        ),
      ),
    );
  }, [giftPromotions]);

  const usesCarrierShipping =
    businessType === "retail" || businessType === "wholesale";

  const foodOrderingUnavailable =
    businessType === "food" &&
    foodAvailability != null &&
    !foodAvailability.is_open;

  const [savedCustomer] = useState<SavedCustomer>(() => {
    if (typeof window === "undefined") {
      return {
        customer_name: "",
        customer_phone: "",
      };
    }

    try {
      const savedValue = localStorage.getItem(CUSTOMER_STORAGE_KEY);

      if (!savedValue) {
        return {
          customer_name: "",
          customer_phone: "",
        };
      }

      const parsedCustomer = JSON.parse(savedValue) as Partial<SavedCustomer>;

      return {
        customer_name: parsedCustomer.customer_name ?? "",
        customer_phone: parsedCustomer.customer_phone ?? "",
      };
    } catch {
      localStorage.removeItem(CUSTOMER_STORAGE_KEY);

      return {
        customer_name: "",
        customer_phone: "",
      };
    }
  });

  const [customerName, setCustomerName] = useState(savedCustomer.customer_name);

  const [customerPhone, setCustomerPhone] = useState(
    savedCustomer.customer_phone,
  );

  const [customerNotes, setCustomerNotes] = useState("");

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | "">("");

  const [cashChangeFor, setCashChangeFor] = useState("");

  const [customerCep, setCustomerCep] = useState("");

  const [customerAddress, setCustomerAddress] = useState("");

  const [customerNumber, setCustomerNumber] = useState("");

  const [customerComplement, setCustomerComplement] = useState("");

  const [customerDistrict, setCustomerDistrict] = useState("");

  const [customerCity, setCustomerCity] = useState("");

  const [customerState, setCustomerState] = useState("");

  const [isLookingUpPostalCode, setIsLookingUpPostalCode] = useState(false);

  const [postalCodeMessage, setPostalCodeMessage] = useState("");

  const [foodDeliveryType, setFoodDeliveryType] = useState<FoodDeliveryType>(
    () => {
      if (localDeliveryEnabled) {
        return "local_delivery";
      }

      return "pickup";
    },
  );

  const [selectedDeliveryRegionId, setSelectedDeliveryRegionId] = useState<
    number | null
  >(null);

  const postalAddress = useRef({ address: customerAddress, district: customerDistrict, city: customerCity, state: customerState });
  useEffect(() => {
    postalAddress.current = { address: customerAddress, district: customerDistrict, city: customerCity, state: customerState };
  }, [customerAddress, customerDistrict, customerCity, customerState]);
  const postalDigits = customerCep.replace(/\D/g, "");
  const onlineLoaded = online !== null;
  const addressLookupMode = online?.address_lookup_mode;
  const devDeliveryMode = online?.dev_delivery?.mode;

  useEffect(() => {
    if (businessType === "food" && (!onlineLoaded || foodDeliveryType === "pickup")) {
      setIsLookingUpPostalCode(false);
      return;
    }
    if (devDeliveryMode === "dev_fixture") {
      setIsLookingUpPostalCode(false);
      setPostalCodeMessage("Endereço sintético DEV: confira os campos selecionados. Nenhum endereço é enviado a serviços externos.");
      return;
    }
    const digits = postalDigits;

    if (digits.length !== 8) {
      setIsLookingUpPostalCode(false);
      setPostalCodeMessage("");
      return;
    }

    let isCancelled = false;
    let timedOut = false;
    const controller = new AbortController();
    const original = postalAddress.current;
    const deadline = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
      if (!isCancelled) {
        setIsLookingUpPostalCode(false);
        setPostalCodeMessage("A consulta de CEP demorou mais que o esperado. Você pode preencher o endereço manualmente.");
      }
    }, 15000);

    async function loadAddress() {
      setIsLookingUpPostalCode(true);
      setPostalCodeMessage("");

      try {
        const result = await lookupPostalCode(digits, controller.signal,
          addressLookupMode === "dev_checkout" ? slug : undefined);

        if (isCancelled || timedOut) {
          return;
        }

        setCustomerAddress(current => current === original.address ? result.address || "" : current);

        setCustomerDistrict(current => current === original.district ? result.district || "" : current);

        setCustomerCity(current => current === original.city ? result.city || "" : current);

        setCustomerState(current => current === original.state ? result.state || "" : current);

        setPostalCodeMessage(
          result.address
            ? "Endereço encontrado. Confira os dados e informe o número."
            : "CEP localizado. Complete rua, número e bairro e confira a cidade e a UF.",
        );
      } catch {
        if (isCancelled || timedOut) {
          return;
        }

        setPostalCodeMessage(
          "Não foi possível localizar esse CEP. Você pode preencher o endereço manualmente.",
        );
      } finally {
        window.clearTimeout(deadline);
        if (!isCancelled) {
          setIsLookingUpPostalCode(false);
        }
      }
    }

    void loadAddress();

    return () => {
      isCancelled = true;
      window.clearTimeout(deadline);
      controller.abort();
    };
  }, [businessType, foodDeliveryType, postalDigits, onlineLoaded, addressLookupMode, devDeliveryMode, slug]);

  useEffect(() => {
    if (
      businessType !== "food" ||
      deliveryFeeMode !== "region" ||
      foodDeliveryType !== "local_delivery"
    ) {
      return;
    }

    const district = normalizeDeliveryRegionName(customerDistrict);

    /*
     * Sempre limpamos a região anterior.
     * Isso impede que um CEP novo herde a taxa
     * de um bairro consultado anteriormente.
     */
    if (!district) {
      setSelectedDeliveryRegionId(null);
      return;
    }

    const matchedRegion = deliveryRegions.find(
      (region) => normalizeDeliveryRegionName(region.name) === district,
    );

    setSelectedDeliveryRegionId(matchedRegion?.id ?? null);
  }, [
    businessType,
    customerDistrict,
    deliveryFeeMode,
    online?.radius_delivery,
    deliveryRegions,
    foodDeliveryType,
  ]);

  const [errorMessage, setErrorMessage] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [pixCheckout, setPixCheckout] = useState<PixCheckoutState | null>(null);

  const [pixCopied, setPixCopied] = useState(false);

  const [isMobileSummaryOpen, setIsMobileSummaryOpen] = useState(false);

  const [shippingOptions, setShippingOptions] = useState<ShippingQuoteOption[]>(
    [],
  );

  const [selectedShipping, setSelectedShipping] =
    useState<ShippingQuoteOption | null>(null);

  const [shippingErrorMessage, setShippingErrorMessage] = useState("");

  const [isCalculatingShipping, setIsCalculatingShipping] = useState(false);

  const selectedDeliveryRegion =
    deliveryRegions.find((region) => region.id === selectedDeliveryRegionId) ??
    null;

  const deliveryAddress = { customer_address: customerAddress.trim(), customer_number: customerNumber.trim(), customer_district: customerDistrict.trim(), customer_city: customerCity.trim(), customer_state: customerState.trim().toUpperCase(), customer_cep: postalDigits };
  const addressKey = JSON.stringify(deliveryAddress);
  const deliveryAddressComplete = customerAddress.trim().length >= 2 && customerNumber.trim() !== "" && customerDistrict.trim().length >= 2 && customerCity.trim().length >= 2 && /^[A-Za-z]{2}$/.test(customerState.trim()) && customerCep.replace(/\D/g, "").length === 8;
  useEffect(() => {
    let active = true;
    if (businessType === "food") void publicOnlineSettings(slug).then(value => { if (active) setOnline(value); }).catch(() => { if (active) setOnline(null); });
    return () => { active = false; };
  }, [businessType, slug]);
  const requiresDeliveryQuote = businessType === "food" && Boolean(online?.radius_delivery) && foodDeliveryType === "local_delivery";
  const deliveryRulesKey = JSON.stringify([online?.delivery_bands, online?.inner_km, online?.outer_km, online?.inner_fee, online?.outer_fee, online?.maximum_distance_km]);
  const radius = useDeliveryQuote(slug, addressKey, requiresDeliveryQuote, deliveryAddressComplete, deliveryRulesKey);
  const radiusReady = radius.ready;

  const foodDeliveryFee =
    businessType === "food" && foodDeliveryType === "local_delivery"
      ? online?.radius_delivery ? Number(radiusReady ? radius?.fee : 0) : deliveryFeeMode === "fixed"
        ? Number(localDeliveryFee) || 0
        : Number(selectedDeliveryRegion?.fee ?? 0)
      : 0;

  const [couponCode, setCouponCode] = useState("");

  const [appliedCoupon, setAppliedCoupon] =
    useState<PublicCouponValidationResponse | null>(null);

  const [couponErrorMessage, setCouponErrorMessage] = useState("");

  const [couponSuccessMessage, setCouponSuccessMessage] = useState("");

  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  function clearShippingState() {
    setShippingOptions([]);
    setSelectedShipping(null);
    setShippingErrorMessage("");
  }

  function handleFoodDeliveryTypeChange(type: FoodDeliveryType) {
    setFoodDeliveryType(type);
    setErrorMessage("");

    if (type === "pickup") {
      setSelectedDeliveryRegionId(null);
      clearShippingState();
    }
  }

  function clearCouponState({
    clearCode = false,
  }: {
    clearCode?: boolean;
  } = {}) {
    setAppliedCoupon(null);
    setCouponErrorMessage("");
    setCouponSuccessMessage("");

    if (clearCode) {
      setCouponCode("");
    }
  }

  function handleCouponCodeChange(value: string) {
    setCouponCode(value.toUpperCase());
    setAppliedCoupon(null);
    setCouponErrorMessage("");
    setCouponSuccessMessage("");
  }

  function handleUpdateCartQuantity(itemKey: string, quantity: number) {
    clearCouponState();
    clearShippingState();
    onUpdateQuantity(itemKey, quantity);
  }

  function handleRemoveCartItem(itemKey: string) {
    clearCouponState();
    clearShippingState();
    onRemove(itemKey);
  }

  function handleClearCart() {
    clearCouponState({
      clearCode: true,
    });
    clearShippingState();

    onClear();
  }

  async function handleCalculateShipping() {
    const normalizedCep = customerCep.replace(/\D/g, "");

    setShippingErrorMessage("");
    setShippingOptions([]);
    setSelectedShipping(null);

    if (normalizedCep.length !== 8) {
      setShippingErrorMessage("Informe um CEP válido.");
      return;
    }

    if (items.length === 0) {
      setShippingErrorMessage("Adicione produtos ao carrinho.");
      return;
    }

    setIsCalculatingShipping(true);

    try {
      const response = await quoteShipping(slug, {
        postal_code: normalizedCep,
        items: items.map(item => ({product:item.product.id, variation:item.variation?.id ?? null, quantity:item.quantity})),
      });

      setShippingOptions(response.options);

      if (response.options.length === 1) {
        setSelectedShipping(response.options[0]);
      }
    } catch (error: unknown) {
      setShippingErrorMessage(getApiErrorMessage(error));
    } finally {
      setIsCalculatingShipping(false);
    }
  }

  async function handleApplyCoupon() {
    const normalizedCode = couponCode.trim().toUpperCase();

    setCouponErrorMessage("");
    setCouponSuccessMessage("");
    setAppliedCoupon(null);

    if (!normalizedCode) {
      setCouponErrorMessage("Informe o código do cupom.");
      return;
    }

    if (!Number.isFinite(total) || total <= 0) {
      setCouponErrorMessage("O subtotal do pedido é inválido.");
      return;
    }

    setIsValidatingCoupon(true);

    try {
      const response = await validatePublicCoupon(slug, {
        code: normalizedCode,
        subtotal: total,
      });

      setCouponCode(response.coupon.code);
      setAppliedCoupon(response);
      setCouponSuccessMessage(`Cupom ${response.coupon.code} aplicado.`);
    } catch (error: unknown) {
      setCouponErrorMessage(getApiErrorMessage(error));
    } finally {
      setIsValidatingCoupon(false);
    }
  }

  const foodCustomerDetailsComplete = (() => {
    if (businessType !== "food") {
      return true;
    }

    const normalizedName = customerName.trim();
    const normalizedPhone = customerPhone.replace(/\D/g, "");
    const normalizedCep = customerCep.replace(/\D/g, "");
    const normalizedAddress = customerAddress.trim();
    const normalizedNumber = customerNumber.trim();
    const normalizedDistrict = customerDistrict.trim();
    const normalizedCity = customerCity.trim();
    const normalizedState = customerState.trim().toUpperCase();

    if (normalizedName.length < 2 || normalizedPhone.length < 10) {
      return false;
    }

    const requiresAddress = foodDeliveryType !== "pickup";

    if (!requiresAddress) {
      return true;
    }

    if (
      normalizedCep.length !== 8 ||
      normalizedAddress.length < 2 ||
      !normalizedNumber ||
      normalizedDistrict.length < 2 ||
      normalizedCity.length < 2 ||
      normalizedState.length !== 2 ||
      !/^[A-Z]{2}$/.test(normalizedState)
    ) {
      return false;
    }

    if (
      foodDeliveryType === "local_delivery" &&
      !online?.radius_delivery && deliveryFeeMode === "region" &&
      selectedDeliveryRegionId === null
    ) {
      return false;
    }

    return true;
  })();

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage("");

    const normalizedName = customerName.trim();

    const normalizedPhone = customerPhone.replace(/\D/g, "");

    const normalizedCep = customerCep.replace(/\D/g, "");

    const normalizedAddress = customerAddress.trim();

    const normalizedNumber = customerNumber.trim();

    const normalizedComplement = customerComplement.trim();

    const normalizedDistrict = customerDistrict.trim();

    const normalizedCity = customerCity.trim();

    const normalizedState = customerState.trim().toUpperCase();

    if (normalizedName.length < 2) {
      setErrorMessage("Informe o nome do cliente.");
      return;
    }

    if (normalizedPhone.length < 10) {
      setErrorMessage("Informe um telefone válido.");
      return;
    }

    const requiresAddress = !(
      businessType === "food" && foodDeliveryType === "pickup"
    );

    if (requiresAddress && normalizedCep.length !== 8) {
      setErrorMessage("Informe um CEP válido.");
      return;
    }

    if (requiresAddress && normalizedAddress.length < 2) {
      setErrorMessage("Informe o endereço.");
      return;
    }

    if (requiresAddress && !normalizedNumber) {
      setErrorMessage("Informe o número do endereço.");
      return;
    }

    if (requiresAddress && normalizedDistrict.length < 2) {
      setErrorMessage("Informe o bairro.");
      return;
    }

    if (requiresAddress && normalizedCity.length < 2) {
      setErrorMessage("Informe a cidade.");
      return;
    }

    if (
      requiresAddress &&
      (normalizedState.length !== 2 || !/^[A-Z]{2}$/.test(normalizedState))
    ) {
      setErrorMessage("Informe uma UF válida.");
      return;
    }

    if (
      businessType === "food" &&
      foodDeliveryType === "local_delivery" &&
      !online?.radius_delivery && deliveryFeeMode === "region" &&
      selectedDeliveryRegionId === null
    ) {
      setErrorMessage(
        customerDistrict.trim()
          ? "Entrega indisponível para este bairro. Escolha retirada no local ou informe outro endereço."
          : "Informe o CEP para verificar se há entrega disponível para sua região.",
      );
      return;
    }

    const hasPaymentMethods =
      acceptPix || acceptCardOnDelivery || acceptCashOnDelivery;

    if (!hasPaymentMethods) {
      setErrorMessage("Esta empresa ainda não configurou formas de pagamento.");
      return;
    }

    if (!paymentMethod) {
      setErrorMessage("Selecione uma forma de pagamento.");
      return;
    }

    if (businessType === "food" && !online) {
      setErrorMessage("Não foi possível consultar as condições de checkout. Reabra o carrinho para tentar novamente.");
      void publicOnlineSettings(slug).then(setOnline).catch(() => {});
      return;
    }
    if (businessType === "food" && online?.radius_delivery && foodDeliveryType === "local_delivery" && !radiusReady) {
      // Pending delivery is a live status, never a persistent submission error.
      // The footer also disables submission until this address has a valid quote.
      return;
    }
    if (paymentMethod === "pix" && (!acceptPix || (!online?.pix_online && !pixKey.trim()))) {
      setErrorMessage("PIX não está disponível para este pedido.");
      return;
    }

    if (paymentMethod === "card_on_delivery" && !acceptCardOnDelivery) {
      setErrorMessage(
        "Cartão na entrega não está disponível para este pedido.",
      );
      return;
    }

    if (paymentMethod === "cash_on_delivery" && !acceptCashOnDelivery) {
      setErrorMessage(
        "Dinheiro na entrega não está disponível para este pedido.",
      );
      return;
    }

    const normalizedCashChangeFor = cashChangeFor.trim().replace(",", ".");

    if (paymentMethod === "cash_on_delivery" && normalizedCashChangeFor) {
      const changeValue = Number(normalizedCashChangeFor);

      if (!Number.isFinite(changeValue) || changeValue <= 0) {
        setErrorMessage("Informe um valor válido para o troco.");
        return;
      }
    }

    if (items.length === 0) {
      setErrorMessage("Adicione pelo menos um produto ao pedido.");
      return;
    }

    if (foodOrderingUnavailable) {
      setErrorMessage(
        foodAvailability?.message ||
          "A loja não está recebendo pedidos no momento.",
      );
      return;
    }

    if (quotePending || quoteError) {
      setErrorMessage(quoteError ?? "Aguarde a atualização dos preços."); return;
    }
    if (onRevalidate) {
      setIsSubmitting(true);
      try {
        if (!await onRevalidate()) {setAppliedCoupon(null); setErrorMessage("O preço do cardápio mudou. Confira o novo total e finalize novamente."); return;}
      } catch (error) {setErrorMessage(getApiErrorMessage(error)); return;}
      finally {setIsSubmitting(false);}
    }
    const undecidedGiftPromotion =
      giftPromotions.find(
        (promotion) =>
          giftChoices[promotion.public_id] ===
          undefined,
      );

    if (undecidedGiftPromotion) {
      setErrorMessage(
        "Escolha se deseja receber o brinde antes de finalizar o pedido.",
      );
      return;
    }

    const acceptedGiftPromotions =
      giftPromotions
        .filter(
          (promotion) =>
            giftChoices[promotion.public_id] ===
            true,
        )
        .map(
          (promotion) =>
            promotion.public_id,
        );

    const payload: PublicOrderPayload = {
      customer_name: normalizedName,
      customer_phone: normalizedPhone,
      customer_notes: customerNotes.trim(),
      customer_cep: formatCep(normalizedCep),
      customer_address: normalizedAddress,
      customer_number: normalizedNumber,
      customer_complement: normalizedComplement,
      customer_district: normalizedDistrict,
      customer_city: normalizedCity,
      customer_state: normalizedState,
      coupon_code: appliedCoupon?.coupon.code,
      accepted_gift_promotions: acceptedGiftPromotions,

      delivery_type: businessType === "food" ? foodDeliveryType : "shipping",

      delivery_region_id:
        businessType === "food" &&
        foodDeliveryType === "local_delivery" &&
        deliveryFeeMode === "region"
          ? selectedDeliveryRegionId
          : undefined,

      shipping_service_id:
        usesCarrierShipping && selectedShipping?.id != null
          ? String(selectedShipping.id)
          : undefined,

      payment_method: paymentMethod,
      ...(businessType === "food" && paymentMethod === "pix" ? { online_pix: Boolean(online?.pix_online) } : {}),
      expected_total: businessType === "food" && (paymentMethod !== "pix" || online?.pix_online)
        ? (Number(appliedCoupon?.total ?? total) + foodDeliveryFee).toFixed(2)
        : undefined,

      cash_change_for:
        paymentMethod === "cash_on_delivery" && cashChangeFor.trim()
          ? cashChangeFor.trim().replace(",", ".")
          : undefined,

      items: items.map(cartItemPayload),
    };

    /*
     * PIX:
     * valida e calcula primeiro.
     * Nenhum pedido é criado.
     */
    if (paymentMethod === "pix" && !online?.pix_online) {
      setIsSubmitting(true);

      try {
        const preview = await previewPublicOrderPix(slug, payload);

        let pixPayload: string | null = null;

        let qrDataUrl: string | null = null;

        try {
          pixPayload = buildPixPayload({
            pixKey: preview.pix_key,
            merchantName: companyName,
            merchantCity: companyCity,
            amount: preview.total,
            txid: "",
          });

          qrDataUrl = await QRCode.toDataURL(pixPayload, {
            errorCorrectionLevel: "M",
            margin: 2,
            width: 320,
          });
        } catch (pixError) {
          console.error("Não foi possível gerar o QR Code PIX.", pixError);
        }

        setPixCopied(false);

        setPixCheckout({
          payload,
          total: preview.total,
          pixPayload,
          qrDataUrl,
          pixKey: preview.pix_key,
        });
      } catch (error: unknown) {
        setErrorMessage(getApiErrorMessage(error));
      } finally {
        setIsSubmitting(false);
      }

      return;
    }

    /*
     * Cartão/dinheiro:
     * registra o pedido diretamente na plataforma.
     */
    setIsSubmitting(true);

    try {
      const order = await createPublicOrder(slug, payload, businessType === "food");

      localStorage.setItem(
        CUSTOMER_STORAGE_KEY,
        JSON.stringify({
          customer_name: order.customer_name,
          customer_phone: order.customer_phone,
        } satisfies SavedCustomer),
      );

      const trackingPath = order.tracking_path;

      setCustomerNotes("");
      setPaymentMethod("");
      setCashChangeFor("");
      setCustomerCep("");
      setCustomerAddress("");
      setCustomerNumber("");
      setCustomerComplement("");
      setCustomerDistrict("");
      setCustomerCity("");
      setCustomerState("");

      clearCouponState({
        clearCode: true,
      });

      onClear();
      onClose();

      window.location.href = trackingPath;
    } catch (error: unknown) {
      setErrorMessage(getApiErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function finalizePixCheckout() {
    if (!pixCheckout || isSubmitting) {
      return;
    }

    setErrorMessage("");

    if (foodOrderingUnavailable) {
      setPixCheckout(null);
      setErrorMessage(
        foodAvailability?.message ||
          "A loja não está recebendo pedidos no momento.",
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const order = await createPublicOrder(slug, {
        ...pixCheckout.payload,
        expected_total: pixCheckout.total,
      }, businessType === "food");

      localStorage.setItem(
        CUSTOMER_STORAGE_KEY,
        JSON.stringify({
          customer_name: order.customer_name,
          customer_phone: order.customer_phone,
        } satisfies SavedCustomer),
      );

      const trackingPath = order.tracking_path;

      setPixCheckout(null);

      setCustomerNotes("");
      setPaymentMethod("");
      setCashChangeFor("");
      setCustomerCep("");
      setCustomerAddress("");
      setCustomerNumber("");
      setCustomerComplement("");
      setCustomerDistrict("");
      setCustomerCity("");
      setCustomerState("");

      clearCouponState({
        clearCode: true,
      });

      onClear();
      onClose();

      window.location.href = trackingPath;
    } catch (error: unknown) {
      setErrorMessage(getApiErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-sm">
      <button
        type="button"
        aria-label="Fechar carrinho"
        onClick={onClose}
        className="absolute inset-0 h-full w-full"
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-lg flex-col bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
          <div>
            <h2 className="text-xl font-black text-slate-950">Seu pedido</h2>

            <p className="mt-1 text-sm text-slate-500">
              Revise os produtos e informe seus dados.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={21} />
          </button>
        </header>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-blue-50 text-blue-600">
              <ShoppingCart size={30} />
            </div>

            <h3 className="mt-5 text-xl font-bold text-slate-950">
              Seu pedido está vazio
            </h3>

            <p className="mt-2 max-w-xs text-sm leading-6 text-slate-500">
              Adicione produtos do catálogo para montar sua solicitação.
            </p>

            <button
              type="button"
              onClick={onClose}
              className="mt-6 h-11 rounded-2xl bg-blue-600 px-5 text-sm font-bold text-white"
            >
              Continuar escolhendo
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="flex-1 space-y-4 overflow-y-auto p-5 sm:p-6">
              {items.map((item) => {
                const itemKey = getCartItemKey(item);

                const unitPrice = getCartItemUnitPrice(item);

                const subtotal = unitPrice * item.quantity;

                return (
                  <article
                    key={itemKey}
                    className="flex gap-4 rounded-3xl border border-slate-200 p-4"
                  >
                    <div
                      className="h-20 w-20 shrink-0 rounded-2xl bg-slate-100 bg-cover bg-center"
                      style={
                        item.product.primary_image_url
                          ? {
                              backgroundImage: `url("${item.product.primary_image_url}")`,
                            }
                          : undefined
                      }
                    >
                      {!item.product.primary_image_url && (
                        <div className="flex h-full items-center justify-center text-slate-300">
                          <Package size={26} />
                        </div>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="line-clamp-2 text-sm font-bold text-slate-950">
                            {item.product.name}
                          </h3>

                          {!item.variation && item.product.size && (
                            <p className="mt-1 text-xs text-slate-500">
                              <span className="font-semibold text-slate-600">
                                Tamanho:
                              </span>{" "}
                              {item.product.size}
                            </p>
                          )}

                          {item.variation && (
                            <div className="mt-1 space-y-0.5 text-xs text-slate-500">
                              {item.variation.values.map((value) => (
                                <p key={value.id}>
                                  <span className="font-semibold text-slate-600">
                                    {value.attribute_name}:
                                  </span>{" "}
                                  {value.value}
                                </p>
                              ))}

                              {item.variation.sku && (
                                <p>
                                  <span className="font-semibold text-slate-600">
                                    SKU:
                                  </span>{" "}
                                  {item.variation.sku}
                                </p>
                              )}
                            </div>
                          )}

                          {(item.selected_options ?? []).length > 0 && (
                            <div className="mt-2 space-y-1">
                              {(item.selected_options ?? []).map((option) => (
                                <p
                                  key={`${option.group_id}:${option.option_id}`}
                                  className="text-xs leading-5 text-slate-500"
                                >
                                  <span className="font-semibold text-slate-600">
                                    {option.group_name}:
                                  </span>{" "}
                                  {fractionLabel(option.fraction)} {option.option_name}
                                  {Number(option.price_delta) > 0 && (
                                    <> + {formatCurrency(option.price_delta)}</>
                                  )}
                                </p>
                              ))}
                            </div>
                          )}

                          {item.observation && (
                            <p className="mt-2 text-xs leading-5 text-slate-500">
                              <span className="font-semibold text-slate-600">
                                Obs.:
                              </span>{" "}
                              {item.observation}
                            </p>
                          )}

                          <p className="mt-2 text-xs font-semibold text-blue-600">
                            {formatCurrency(unitPrice)}
                          </p>
                        </div>

                        <button
                          type="button"
                          title="Remover produto"
                          disabled={isSubmitting}
                          onClick={() => handleRemoveCartItem(itemKey)}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      <div className="mt-4 flex items-center justify-between">
                        <div className="flex items-center rounded-xl border border-slate-200">
                          <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() =>
                              handleUpdateCartQuantity(
                                itemKey,
                                item.quantity - 1,
                              )
                            }
                            className="flex h-9 w-9 items-center justify-center text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Minus size={15} />
                          </button>

                          <span className="min-w-8 text-center text-sm font-bold text-slate-900">
                            {item.quantity}
                          </span>

                          <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() =>
                              handleUpdateCartQuantity(
                                itemKey,
                                item.quantity + 1,
                              )
                            }
                            className="flex h-9 w-9 items-center justify-center text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Plus size={15} />
                          </button>
                        </div>

                        <strong className="text-sm text-slate-950">
                          {formatCurrency(subtotal)}
                        </strong>
                      </div>
                    </div>
                  </article>
                );
              })}

              <button
                type="button"
                disabled={isSubmitting}
                onClick={handleClearCart}
                className="text-sm font-bold text-red-600 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Limpar pedido
              </button>

              {giftPromotions.length > 0 && (
                <div className="space-y-3">
                  {giftPromotions.map((promotion) => {
                    const choice =
                      giftChoices[promotion.public_id];

                    return (
                      <div
                        key={promotion.public_id}
                        className="rounded-3xl border border-emerald-200 bg-emerald-50/70 p-5"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-600 shadow-sm">
                            <Gift size={20} />
                          </div>

                          <div className="min-w-0">
                            <h3 className="text-base font-black text-slate-950">
                              Você ganhou um brinde!
                            </h3>

                            <p className="mt-1 text-sm font-bold text-emerald-700">
                              {promotion.gift.quantity}x{" "}
                              {promotion.gift.product_name}
                              {promotion.gift.variation_name
                                ? ` · ${promotion.gift.variation_name}`
                                : ""}
                            </p>

                            <p className="mt-1 text-xs leading-5 text-slate-500">
                              {promotion.name}
                            </p>
                          </div>
                        </div>

                        <div className="mt-4 grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() =>
                              setGiftChoices((current) => ({
                                ...current,
                                [promotion.public_id]: true,
                              }))
                            }
                            className={
                              choice === true
                                ? "h-11 rounded-xl bg-emerald-600 px-3 text-sm font-black text-white"
                                : "h-11 rounded-xl border border-emerald-200 bg-white px-3 text-sm font-bold text-emerald-700 hover:bg-emerald-50"
                            }
                          >
                            Quero meu brinde
                          </button>

                          <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() =>
                              setGiftChoices((current) => ({
                                ...current,
                                [promotion.public_id]: false,
                              }))
                            }
                            className={
                              choice === false
                                ? "h-11 rounded-xl bg-slate-700 px-3 text-sm font-black text-white"
                                : "h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-600 hover:bg-slate-50"
                            }
                          >
                            Não quero
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="rounded-3xl border border-blue-100 bg-blue-50/60 p-5">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm">
                    <BadgePercent size={20} />
                  </div>

                  <div>
                    <h3 className="text-base font-black text-slate-950">
                      Cupom de desconto
                    </h3>

                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      Informe o código disponibilizado pela empresa.
                    </p>
                  </div>
                </div>

                <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                  <input
                    type="text"
                    value={couponCode}
                    disabled={isSubmitting || isValidatingCoupon}
                    onChange={(event) =>
                      handleCouponCodeChange(event.target.value)
                    }
                    placeholder="Ex.: BEMVINDO10"
                    maxLength={50}
                    autoComplete="off"
                    className="h-12 w-full min-w-0 rounded-2xl border border-slate-200 bg-white px-4 text-sm uppercase text-slate-900 outline-none placeholder:normal-case focus:border-blue-400 focus:ring-4 focus:ring-blue-100 disabled:opacity-60 sm:flex-1"
                  />

                  {appliedCoupon ? (
                    <button
                      type="button"
                      disabled={isSubmitting}
                      onClick={() =>
                        clearCouponState({
                          clearCode: true,
                        })
                      }
                      className="inline-flex h-12 items-center justify-center rounded-2xl border border-red-200 bg-white px-5 text-sm font-bold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Remover
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={
                        isSubmitting || isValidatingCoupon || !couponCode.trim()
                      }
                      onClick={handleApplyCoupon}
                      className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                    >
                      {isValidatingCoupon ? (
                        <>
                          <LoaderCircle size={17} className="animate-spin" />
                          Validando...
                        </>
                      ) : (
                        "Aplicar"
                      )}
                    </button>
                  )}
                </div>

                {couponSuccessMessage && appliedCoupon && (
                  <div className="mt-4 flex items-start gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
                    <CheckCircle2 size={18} className="mt-0.5 shrink-0" />

                    <div>
                      <p>{couponSuccessMessage}</p>

                      {appliedCoupon.coupon.description && (
                        <p className="mt-1 text-xs font-medium leading-5 text-emerald-600">
                          {appliedCoupon.coupon.description}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {couponErrorMessage && (
                  <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                    {couponErrorMessage}
                  </div>
                )}
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                <h3 className="text-base font-black text-slate-950">
                  Seus dados
                </h3>

                <p className="mt-1 text-xs leading-5 text-slate-500">
                  O nome e o telefone ficarão salvos neste navegador para os
                  próximos pedidos.
                </p>

                <div className="mt-5 space-y-4">
                  <label className="block">
                    <span className="mb-2 block text-sm font-bold text-slate-700">
                      Nome
                    </span>

                    <input
                      type="text"
                      value={customerName}
                      disabled={isSubmitting}
                      onChange={(event) => setCustomerName(event.target.value)}
                      placeholder="Seu nome"
                      maxLength={150}
                      autoComplete="name"
                      className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100 disabled:opacity-60"
                    />
                  </label>

                  <label className="block">
                    <span className="mb-2 block text-sm font-bold text-slate-700">
                      Telefone
                    </span>

                    <input
                      type="tel"
                      value={customerPhone}
                      disabled={isSubmitting}
                      onChange={(event) => setCustomerPhone(event.target.value)}
                      placeholder="(11) 99999-9999"
                      maxLength={20}
                      autoComplete="tel"
                      className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100 disabled:opacity-60"
                    />
                  </label>

                  {businessType === "food" && (
                    <div className="border-t border-slate-200 pt-5">
                      <h4 className="text-sm font-black text-slate-900">
                        Como deseja receber?
                      </h4>

                      <p className="mt-1 text-xs leading-5 text-slate-500">
                        Escolha a forma de recebimento do pedido.
                      </p>

                      <div className="mt-4 grid gap-3">
                        {localDeliveryEnabled && (
                          <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => {
                              handleFoodDeliveryTypeChange("local_delivery");
                              clearShippingState();
                            }}
                            className={`rounded-2xl border px-4 py-4 text-left transition ${
                              foodDeliveryType === "local_delivery"
                                ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100"
                                : "border-slate-200 bg-white hover:border-blue-300"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-4">
                              <div>
                                <p className="font-black text-slate-900">
                                  Entrega no endereço
                                </p>

                                <p className="mt-1 text-xs leading-5 text-slate-500">
                                  Receba seu pedido no endereço informado.
                                </p>
                              </div>

                              {!online?.radius_delivery && deliveryFeeMode === "fixed" && (
                                <span className="shrink-0 text-sm font-black text-blue-600">
                                  {Number(localDeliveryFee) > 0
                                    ? formatCurrency(localDeliveryFee)
                                    : "Grátis"}
                                </span>
                              )}
                            </div>
                          </button>
                        )}

                        {pickupEnabled && (
                          <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => {
                              handleFoodDeliveryTypeChange("pickup");
                              setSelectedDeliveryRegionId(null);
                              clearShippingState();
                            }}
                            className={`rounded-2xl border px-4 py-4 text-left transition ${
                              foodDeliveryType === "pickup"
                                ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100"
                                : "border-slate-200 bg-white hover:border-blue-300"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-4">
                              <div>
                                <p className="font-black text-slate-900">
                                  Retirada no local
                                </p>

                                <p className="mt-1 text-xs leading-5 text-slate-500">
                                  Retire o pedido diretamente no
                                  estabelecimento.
                                </p>
                              </div>

                              <span className="shrink-0 text-sm font-black text-emerald-600">
                                Grátis
                              </span>
                            </div>
                          </button>
                        )}
                      </div>

                      {localDeliveryEnabled &&
                        foodDeliveryType === "local_delivery" &&
                        !online?.radius_delivery && deliveryFeeMode === "region" && (
                          <div className="mt-4">
                            <label className="block">
                              <span className="mb-2 block text-sm font-bold text-slate-700">
                                Região de entrega
                              </span>

                              {selectedDeliveryRegion ? (
                                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                                  <div className="flex items-center justify-between gap-4">
                                    <div>
                                      <p className="text-sm font-bold text-emerald-800">
                                        Entrega disponível
                                      </p>

                                      <p className="mt-0.5 text-xs text-emerald-700">
                                        {selectedDeliveryRegion.name}
                                      </p>
                                    </div>

                                    <span className="shrink-0 text-sm font-black text-emerald-700">
                                      {formatCurrency(
                                        selectedDeliveryRegion.fee,
                                      )}
                                    </span>
                                  </div>
                                </div>
                              ) : customerDistrict.trim() &&
                                customerCep.replace(/\D/g, "").length === 8 &&
                                !isLookingUpPostalCode ? (
                                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                                  <p className="text-sm font-bold text-amber-800">
                                    Entrega indisponível para este bairro
                                  </p>

                                  <p className="mt-1 text-xs leading-5 text-amber-700">
                                    No momento não há uma região de entrega
                                    cadastrada para {customerDistrict}. Você
                                    pode escolher retirada no local, quando
                                    disponível, ou informar outro endereço.
                                  </p>
                                </div>
                              ) : (
                                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                                  <p className="text-xs leading-5 text-slate-500">
                                    Informe o CEP abaixo para verificarmos
                                    automaticamente a disponibilidade e a taxa
                                    de entrega.
                                  </p>
                                </div>
                              )}

                              <p className="mt-2 text-xs leading-5 text-slate-500">
                                A região e a taxa são identificadas
                                automaticamente pelo bairro retornado pelo CEP.
                              </p>
                            </label>
                          </div>
                        )}
                    </div>
                  )}

                  {!(
                    businessType === "food" && foodDeliveryType === "pickup"
                  ) && (
                    <>
                      <div className="border-t border-slate-200 pt-5">
                        <h4 className="text-sm font-black text-slate-900">
                          Endereço para entrega
                        </h4>

                        <p className="mt-1 text-xs leading-5 text-slate-500">
                          Esses dados serão enviados junto com o pedido para
                          facilitar o cálculo do frete e a entrega.
                        </p>
                      </div>

                      {businessType === "food" && online?.dev_delivery && <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                        <p className="text-sm font-bold text-amber-900">DEV — entrega simulada</p>
                        <p className="mt-1 text-xs leading-5 text-amber-800">Este ambiente está isolado. Use um endereço sintético abaixo para testar a entrega. Nenhum endereço é enviado a provedores externos.</p>
                        <label className="mt-3 block text-sm font-bold text-slate-700">Endereço sintético de teste
                          <select disabled={isSubmitting} value={online.dev_delivery.addresses.find(row => row.customer_address === customerAddress && row.customer_number === customerNumber && row.customer_district === customerDistrict && row.customer_city === customerCity && row.customer_state === customerState && row.customer_cep === customerCep.replace(/\D/g, ""))?.id ?? ""} onChange={event => {
                            const address = online.dev_delivery?.addresses.find(row => row.id === event.target.value);
                            if (!address) return;
                            setCustomerAddress(address.customer_address); setCustomerNumber(address.customer_number);
                            setCustomerDistrict(address.customer_district); setCustomerCity(address.customer_city);
                            setCustomerState(address.customer_state); setCustomerCep(formatCep(address.customer_cep));
                            setCustomerComplement(""); setErrorMessage("");
                          }} className="mt-2 h-12 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-normal text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100">
                            <option value="">Selecione um endereço de teste</option>
                            {online.dev_delivery.addresses.map(address => <option key={address.id} value={address.id}>{address.label}</option>)}
                          </select>
                        </label>
                      </div>}

                      <div className="grid gap-4 sm:grid-cols-2">
                        <label className="block">
                          <span className="mb-2 block text-sm font-bold text-slate-700">
                            CEP
                          </span>

                          <input
                            type="text"
                            inputMode="numeric"
                            value={customerCep}
                            disabled={isSubmitting}
                            onChange={(event) => {
                              setCustomerCep(formatCep(event.target.value));
                              clearShippingState();
                            }}
                            placeholder="00000-000"
                            maxLength={9}
                            autoComplete="postal-code"
                            className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100 disabled:opacity-60"
                          />

                          {(isLookingUpPostalCode || postalCodeMessage) && (
                            <p
                              className={
                                isLookingUpPostalCode
                                  ? "mt-2 text-xs font-semibold text-blue-600"
                                  : "mt-2 text-xs leading-5 text-slate-500"
                              }
                            >
                              {isLookingUpPostalCode
                                ? "Buscando endereço..."
                                : postalCodeMessage}
                            </p>
                          )}
                        </label>

                        {usesCarrierShipping && (
                          <div className="sm:col-span-2">
                            <button
                              type="button"
                              onClick={handleCalculateShipping}
                              disabled={isCalculatingShipping || isSubmitting}
                              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-4 text-sm font-bold text-blue-700 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {isCalculatingShipping ? (
                                <>
                                  <LoaderCircle
                                    size={18}
                                    className="animate-spin"
                                  />
                                  Calculando frete...
                                </>
                              ) : (
                                <>
                                  <Package size={18} />
                                  Calcular frete
                                </>
                              )}
                            </button>

                            {shippingErrorMessage && (
                              <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                                {shippingErrorMessage}
                              </div>
                            )}

                            {shippingOptions.length > 0 && (
                              <div className="mt-4 space-y-3">
                                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                                  Opções de entrega
                                </p>

                                <button
                                  type="button"
                                  onClick={() =>
                                    setSelectedShipping({
                                      id: "pickup",
                                      name: "Retirada no local",
                                      company: "",
                                      price: "0.00",
                                      delivery_time: null,
                                    })
                                  }
                                  className={`flex w-full items-center justify-between gap-4 rounded-2xl border px-4 py-4 text-left transition ${
                                    selectedShipping?.id === "pickup"
                                      ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100"
                                      : "border-slate-200 bg-white hover:border-blue-300"
                                  }`}
                                >
                                  <div className="min-w-0">
                                    <p className="font-black text-slate-900">
                                      Retirada no local
                                    </p>

                                    <p className="mt-1 text-xs text-slate-500">
                                      Combine com o vendedor
                                    </p>
                                  </div>

                                  <strong className="shrink-0 text-base font-black text-emerald-600">
                                    Grátis
                                  </strong>
                                </button>

                                {shippingOptions.map((option) => {
                                  const isSelected =
                                    selectedShipping?.id === option.id;

                                  return (
                                    <button
                                      key={String(option.id)}
                                      type="button"
                                      onClick={() =>
                                        setSelectedShipping(option)
                                      }
                                      className={`flex w-full items-center justify-between gap-4 rounded-2xl border px-4 py-4 text-left transition ${
                                        isSelected
                                          ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100"
                                          : "border-slate-200 bg-white hover:border-blue-300"
                                      }`}
                                    >
                                      <div className="min-w-0">
                                        <p className="font-black text-slate-900">
                                          {option.name}
                                        </p>

                                        <p className="mt-1 text-xs text-slate-500">
                                          {option.company || "Transportadora"}

                                          {option.delivery_time
                                            ? ` · estimativa de ${option.delivery_time} dia${option.delivery_time === 1 ? "" : "s"} úteis`
                                            : ""}
                                        </p>
                                      </div>

                                      <strong className="shrink-0 text-base font-black text-slate-950">
                                        {formatCurrency(option.price)}
                                      </strong>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        <label className="block">
                          <span className="mb-2 block text-sm font-bold text-slate-700">
                            Número
                          </span>

                          <input
                            type="text"
                            value={customerNumber}
                            disabled={isSubmitting}
                            onChange={(event) =>
                              setCustomerNumber(event.target.value)
                            }
                            placeholder="123"
                            maxLength={20}
                            autoComplete="address-line2"
                            className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100 disabled:opacity-60"
                          />
                        </label>
                      </div>

                      <label className="block">
                        <span className="mb-2 block text-sm font-bold text-slate-700">
                          Endereço
                        </span>

                        <input
                          type="text"
                          value={customerAddress}
                          disabled={isSubmitting}
                          onChange={(event) =>
                            setCustomerAddress(event.target.value)
                          }
                          placeholder="Rua, avenida ou rodovia"
                          maxLength={180}
                          autoComplete="address-line1"
                          className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100 disabled:opacity-60"
                        />
                      </label>

                      <label className="block">
                        <span className="mb-2 block text-sm font-bold text-slate-700">
                          Complemento
                          <span className="ml-1 font-medium text-slate-400">
                            (opcional)
                          </span>
                        </span>

                        <input
                          type="text"
                          value={customerComplement}
                          disabled={isSubmitting}
                          onChange={(event) =>
                            setCustomerComplement(event.target.value)
                          }
                          placeholder="Apartamento, bloco, referência..."
                          maxLength={100}
                          className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100 disabled:opacity-60"
                        />
                      </label>

                      <label className="block">
                        <span className="mb-2 block text-sm font-bold text-slate-700">
                          Bairro
                        </span>

                        <input
                          type="text"
                          value={customerDistrict}
                          disabled={isSubmitting}
                          onChange={(event) =>
                            setCustomerDistrict(event.target.value)
                          }
                          placeholder="Seu bairro"
                          maxLength={100}
                          autoComplete="address-level3"
                          className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100 disabled:opacity-60"
                        />
                      </label>

                      <div className="grid gap-4 sm:grid-cols-[1fr_110px]">
                        <label className="block">
                          <span className="mb-2 block text-sm font-bold text-slate-700">
                            Cidade
                          </span>

                          <input
                            type="text"
                            value={customerCity}
                            disabled={isSubmitting}
                            onChange={(event) =>
                              setCustomerCity(event.target.value)
                            }
                            placeholder="Sua cidade"
                            maxLength={100}
                            autoComplete="address-level2"
                            className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100 disabled:opacity-60"
                          />
                        </label>

                        <label className="block">
                          <span className="mb-2 block text-sm font-bold text-slate-700">
                            UF
                          </span>

                          <input
                            type="text"
                            value={customerState}
                            disabled={isSubmitting}
                            onChange={(event) =>
                              setCustomerState(
                                event.target.value
                                  .replace(/[^a-zA-Z]/g, "")
                                  .slice(0, 2)
                                  .toUpperCase(),
                              )
                            }
                            placeholder="SP"
                            maxLength={2}
                            autoComplete="address-level1"
                            className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm uppercase outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100 disabled:opacity-60"
                          />
                        </label>
                      </div>
                  {online?.radius_delivery && foodDeliveryType === "local_delivery" && <div className="mb-3 space-y-2">
                    <p role="status" className={`text-sm ${radiusReady ? "text-emerald-700" : "text-slate-700"}`}>{radiusReady ? `Entrega por raio geográfico: ${formatCurrency(radius.fee ?? "0")}` : radius.error ?? (deliveryAddressComplete ? "Calculando a entrega para este endereço..." : "Preencha CEP, rua, número, bairro, cidade e UF para validar a entrega.")}</p>
                    {!radiusReady && deliveryAddressComplete && radius.error && <button type="button" disabled={isSubmitting} onClick={() => { radius.retry(); setErrorMessage(""); }} className="inline-flex min-h-10 items-center justify-center rounded-xl border border-slate-300 bg-white px-3 text-sm font-bold text-slate-700 hover:border-blue-300 hover:text-blue-700 disabled:opacity-50">Validar endereço novamente</button>}
                  </div>}
                    </>
                  )}

                  <label className="block">
                    <span className="mb-2 block text-sm font-bold text-slate-700">
                      Observações
                    </span>

                    <textarea
                      value={customerNotes}
                      disabled={isSubmitting}
                      onChange={(event) => setCustomerNotes(event.target.value)}
                      placeholder="Ex.: tamanhos, cores, prazo ou informações para entrega."
                      maxLength={2000}
                      rows={4}
                      className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100 disabled:opacity-60"
                    />
                  </label>
                </div>
              </div>
            </div>

            <footer className="border-t border-slate-200 bg-white p-4 sm:p-6">
              <button
                type="button"
                onClick={() => setIsMobileSummaryOpen((current) => !current)}
                aria-expanded={isMobileSummaryOpen}
                className="flex w-full items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-left"
              >
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                    Resumo e pagamento
                  </p>

                  <p className="mt-0.5 text-sm font-semibold text-slate-600">
                    Toque para {isMobileSummaryOpen ? "recolher" : "conferir"}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <strong className="text-base font-black text-slate-950">
                    {formatCurrency(
                      Number(appliedCoupon?.total ?? total) +
                        (businessType === "food"
                          ? foodDeliveryFee
                          : Number(selectedShipping?.price ?? 0)),
                    )}
                  </strong>

                  <ChevronDown
                    size={20}
                    className={`text-slate-500 transition-transform ${
                      isMobileSummaryOpen ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </button>

              <div
                className={`${
                  isMobileSummaryOpen
                    ? "block max-h-[55vh] overflow-y-auto overscroll-contain touch-pan-y pr-1 [-webkit-overflow-scrolling:touch]"
                    : "hidden"
                }`}
              >
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between gap-5">
                    <span className="text-sm font-semibold text-slate-500">
                      Subtotal
                    </span>

                    <span className="font-bold text-slate-700">
                      {formatCurrency(total)}
                    </span>
                  </div>

                  {appliedCoupon && (
                    <>
                      <div className="flex items-center justify-between gap-5 text-emerald-600">
                        <span className="text-sm font-semibold">Desconto</span>

                        <span className="font-black">
                          - {formatCurrency(appliedCoupon.discount_amount)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-5">
                        <span className="text-xs font-bold uppercase tracking-wide text-blue-600">
                          Cupom {appliedCoupon.coupon.code}
                        </span>

                        <span className="text-xs font-semibold text-slate-400">
                          {appliedCoupon.coupon.discount_type_label}
                        </span>
                      </div>
                    </>
                  )}

                  {businessType === "food" ? (
                    <div className="flex items-center justify-between gap-5">
                      <div>
                        <span className="text-sm font-semibold text-slate-500">
                          {foodDeliveryType === "pickup"
                            ? "Retirada"
                            : "Entrega"}
                        </span>

                        <p className="mt-0.5 text-xs text-slate-400">
                          {foodDeliveryType === "pickup"
                            ? "Retirada no local"
                            : online?.radius_delivery ? "Raio geográfico"
                            : deliveryFeeMode === "region"
                              ? selectedDeliveryRegion
                                ? selectedDeliveryRegion.name
                                : "Selecione a região"
                              : "Entrega local"}
                        </p>
                      </div>

                      <span className="font-bold text-slate-700">
                        {foodDeliveryType === "pickup"
                          ? "Grátis"
                          : online?.radius_delivery && !radiusReady ? "A calcular" : formatCurrency(foodDeliveryFee)}
                      </span>
                    </div>
                  ) : (
                    selectedShipping && (
                      <div className="flex items-center justify-between gap-5">
                        <div>
                          <span className="text-sm font-semibold text-slate-500">
                            Frete
                          </span>

                          <p className="mt-0.5 text-xs text-slate-400">
                            {selectedShipping.name}
                            {selectedShipping.delivery_time
                              ? ` · ${selectedShipping.delivery_time} dia${selectedShipping.delivery_time === 1 ? "" : "s"} úteis`
                              : ""}
                          </p>
                        </div>

                        <span className="font-bold text-slate-700">
                          {formatCurrency(selectedShipping.price)}
                        </span>
                      </div>
                    )
                  )}

                  <div className="flex items-center justify-between gap-5 border-t border-slate-200 pt-4">
                    <span className="text-sm font-black text-slate-950">
                      Total estimado
                    </span>

                    <strong className="text-2xl font-black text-slate-950">
                      {formatCurrency(
                        Number(appliedCoupon?.total ?? total) +
                          (businessType === "food"
                            ? foodDeliveryFee
                            : Number(selectedShipping?.price ?? 0)),
                      )}
                    </strong>
                  </div>
                </div>

                <p className="mt-3 text-xs leading-5 text-slate-400">
                  {businessType === "food"
                    ? foodDeliveryType === "pickup"
                      ? "A empresa confirmará o horário e as instruções para retirada do pedido."
                      : "A empresa confirmará os detalhes da entrega."
                    : "O frete e o prazo exibidos são estimativas. A empresa confirmará os detalhes da entrega."}
                </p>

                <section className="rounded-3xl border border-slate-200 p-5">
                  <h3 className="text-sm font-black text-slate-950">
                    Como você quer pagar?
                  </h3>

                  <p
                    className={`mt-1 text-xs leading-5 ${
                      businessType === "food" && !foodCustomerDetailsComplete
                        ? "font-semibold text-amber-700"
                        : "text-slate-500"
                    }`}
                  >
                    {businessType === "food" && !foodCustomerDetailsComplete
                      ? "Preencha seus dados acima para escolher a forma de pagamento."
                      : "Escolha uma das formas de pagamento disponíveis."}
                  </p>

                  {!acceptPix &&
                  !acceptCardOnDelivery &&
                  !acceptCashOnDelivery ? (
                    <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-800">
                      Esta empresa ainda não configurou formas de pagamento.
                    </div>
                  ) : (
                    <div className="mt-4 space-y-3">
                      {acceptPix && (
                        <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 p-4">
                          <input
                            type="radio"
                            name="payment_method"
                            value="pix"
                            checked={paymentMethod === "pix"}
                            disabled={
                              isSubmitting ||
                              (businessType === "food" &&
                                !foodCustomerDetailsComplete)
                            }
                            onChange={() => {
                              setPaymentMethod("pix");
                              setCashChangeFor("");
                              setErrorMessage("");
                            }}
                            className="mt-1 h-4 w-4"
                          />

                          <div>
                            <p className="text-sm font-bold text-slate-900">
                              PIX
                            </p>

                            <p className="mt-1 text-xs leading-5 text-slate-500">
                              Você verá o QR Code e o Pix Copia e Cola antes de
                              finalizar o pedido.
                            </p>
                          </div>
                        </label>
                      )}

                      {acceptCardOnDelivery && (
                        <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 p-4">
                          <input
                            type="radio"
                            name="payment_method"
                            value="card_on_delivery"
                            checked={paymentMethod === "card_on_delivery"}
                            disabled={
                              isSubmitting ||
                              (businessType === "food" &&
                                !foodCustomerDetailsComplete)
                            }
                            onChange={() => {
                              setPaymentMethod("card_on_delivery");
                              setCashChangeFor("");
                              setErrorMessage("");
                            }}
                            className="mt-1 h-4 w-4"
                          />

                          <div>
                            <p className="text-sm font-bold text-slate-900">
                              Cartão na entrega
                            </p>

                            <p className="mt-1 text-xs leading-5 text-slate-500">
                              Pague na maquininha no momento da entrega ou
                              retirada.
                            </p>
                          </div>
                        </label>
                      )}

                      {acceptCashOnDelivery && (
                        <div className="rounded-2xl border border-slate-200 p-4">
                          <label className="flex cursor-pointer items-start gap-3">
                            <input
                              type="radio"
                              name="payment_method"
                              value="cash_on_delivery"
                              checked={paymentMethod === "cash_on_delivery"}
                              disabled={
                                isSubmitting ||
                                (businessType === "food" &&
                                  !foodCustomerDetailsComplete)
                              }
                              onChange={() => {
                                setPaymentMethod("cash_on_delivery");
                                setErrorMessage("");
                              }}
                              className="mt-1 h-4 w-4"
                            />

                            <div>
                              <p className="text-sm font-bold text-slate-900">
                                Dinheiro na entrega
                              </p>

                              <p className="mt-1 text-xs leading-5 text-slate-500">
                                Se precisar de troco, informe o valor abaixo.
                              </p>
                            </div>
                          </label>

                          {paymentMethod === "cash_on_delivery" && (
                            <div className="mt-4">
                              <label className="text-xs font-bold text-slate-700">
                                Troco para quanto?{" "}
                                <span className="font-medium text-slate-400">
                                  (opcional)
                                </span>
                              </label>

                              <div className="mt-2 flex h-12 items-center rounded-2xl border border-slate-200 bg-white px-4">
                                <span className="mr-2 text-sm font-semibold text-slate-500">
                                  R$
                                </span>

                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={cashChangeFor}
                                  onChange={(event) =>
                                    setCashChangeFor(event.target.value)
                                  }
                                  placeholder="Ex.: 100,00"
                                  className="min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </section>
              </div>

              {foodOrderingUnavailable && (
                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <p className="text-sm font-bold text-amber-900">
                    A loja não está recebendo pedidos agora
                  </p>

                  <p className="mt-1 text-xs leading-5 text-amber-800">
                    {foodAvailability?.message ||
                      "Você pode continuar consultando o cardápio e manter os itens no carrinho."}
                  </p>
                </div>
              )}

              {(quotePending || quoteError) && <p role="status" className="mt-3 text-sm text-rose-700">{quoteError ?? "Atualizando preços do pedido…"}</p>}
              {errorMessage && (
                <div className="mt-4 rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                  {errorMessage}
                </div>
              )}

              {businessType === "food" &&
                !foodCustomerDetailsComplete && (
                  <p className="mt-4 text-center text-xs font-semibold text-amber-700">
                    Preencha seus dados para continuar.
                  </p>
                )}

              <button
                type={
                  businessType === "food" && !paymentMethod
                    ? "button"
                    : "submit"
                }
                onClick={() => {
                  if (businessType === "food" && !paymentMethod) {
                    setErrorMessage("");
                    setIsMobileSummaryOpen(true);
                  }
                }}
                disabled={
                  isSubmitting ||
                  foodOrderingUnavailable ||
                  quotePending ||
                  !!quoteError ||
                  (Boolean(paymentMethod) && requiresDeliveryQuote && !radiusReady) ||
                  (businessType === "food" && !foodCustomerDetailsComplete)
                }
                className={`mt-4 inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl text-sm font-bold text-white shadow-lg disabled:cursor-not-allowed sm:mt-5 ${
                  businessType === "food" && !paymentMethod
                    ? "bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300"
                    : "bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <LoaderCircle size={19} className="animate-spin" />
                    Processando...
                  </>
                ) : paymentMethod && requiresDeliveryQuote && radius.pending ? (
                  <><LoaderCircle size={19} className="animate-spin" />Calculando entrega...</>
                ) : businessType === "food" && !paymentMethod ? (
                  "Selecionar forma de pagamento"
                ) : paymentMethod === "pix" ? (
                  "Continuar para o PIX"
                ) : (
                  "Finalizar pedido"
                )}
              </button>
            </footer>
          </form>
        )}
      </aside>

      {pixCheckout && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className="max-h-[95vh] w-full max-w-md overflow-y-auto rounded-3xl bg-white p-5 shadow-2xl sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-emerald-600">
                  Pagamento via PIX
                </p>

                <h2 className="mt-1 text-xl font-black text-slate-950">
                  Finalize seu pedido
                </h2>

                <p className="mt-1 text-sm text-slate-500">
                  Realize o pagamento antes de continuar.
                </p>
              </div>

              <button
                type="button"
                aria-label="Fechar pagamento PIX"
                onClick={() => {
                  setPixCheckout(null);
                  onClose();
                }}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mt-5 rounded-2xl bg-emerald-50 px-4 py-3 text-center">
              <p className="text-xs font-semibold text-emerald-700">
                Total do pedido
              </p>

              <p className="mt-1 text-2xl font-black text-emerald-700">
                {formatCurrency(pixCheckout.total)}
              </p>
            </div>

            <p className="mt-5 text-center text-sm leading-6 text-slate-600">
              {pixCheckout.pixPayload
                ? "Escaneie o QR Code abaixo ou copie o código PIX. Depois, finalize seu pedido."
                : "Use a chave PIX abaixo para realizar o pagamento. Depois, finalize seu pedido."}
            </p>

            {pixCheckout.qrDataUrl && (
              <div className="mt-5 flex justify-center">
                <div className="rounded-3xl border border-slate-200 bg-white p-3 shadow-sm">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={pixCheckout.qrDataUrl}
                    alt="QR Code PIX"
                    className="h-64 w-64"
                  />
                </div>
              </div>
            )}

            <div className="mt-5">
              <p className="text-xs font-bold text-slate-700">
                {pixCheckout.pixPayload ? "Pix Copia e Cola" : "Chave PIX"}
              </p>

              {!pixCheckout.pixPayload && (
                <p className="mt-1 text-xs leading-5 text-amber-700">
                  O QR Code não pôde ser gerado. Use a chave PIX abaixo para
                  realizar o pagamento.
                </p>
              )}

              <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <p className="break-all text-xs leading-5 text-slate-600">
                  {pixCheckout.pixPayload || pixCheckout.pixKey}
                </p>
              </div>

              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(
                      pixCheckout.pixPayload || pixCheckout.pixKey,
                    );

                    setPixCopied(true);

                    window.setTimeout(() => setPixCopied(false), 2000);
                  } catch {
                    setPixCopied(false);
                  }
                }}
                className="mt-3 inline-flex h-11 w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white text-sm font-bold text-slate-700 hover:bg-slate-50"
              >
                {pixCopied ? (
                  <>
                    <CheckCircle2 size={18} className="text-emerald-600" />
                    {pixCheckout.pixPayload
                      ? "Código PIX copiado"
                      : "Chave PIX copiada"}
                  </>
                ) : (
                  <>
                    <Copy size={18} />
                    {pixCheckout.pixPayload
                      ? "Copiar código PIX"
                      : "Copiar chave PIX"}
                  </>
                )}
              </button>
            </div>

            <div className="mt-5 rounded-2xl bg-amber-50 px-4 py-3">
              <p className="text-xs leading-5 text-amber-800">
                O Fornecedor.net não confirma automaticamente o pagamento.
                Finalize o pedido somente após realizar o PIX. Se necessário,
                use o WhatsApp da loja para enviar o comprovante ou falar com a
                empresa.
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                void finalizePixCheckout();
              }}
              className="mt-5 inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-4 text-sm font-bold text-white shadow-lg hover:bg-emerald-600"
            >
              <CheckCircle2 size={19} />
              Já fiz o PIX — finalizar pedido
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
